/**
 * Pure, framework-free age/threshold math for §9.5's operational queues — "Payment-
 * reconciliation PENDING queue aged beyond threshold" and (by the same logic)
 * `sync_conflicts` age. The task brief is explicit that this calculation should be real
 * ("a real 'age > 15 minutes' threshold calculation over mock timestamps, not just static
 * fixture text") even though the *timestamps* it operates on come from
 * `mockPlatformApiClient.ts`'s fixtures — the arithmetic itself has nothing mocked about
 * it and will work identically over real timestamps from a real backend later.
 */

/** Default staleness threshold for both queues in §9.5 — 15 minutes, matching the task
 * brief's example threshold. A real deployment would likely make this configurable per
 * queue; kept as one shared constant here since neither queue's spec text calls for a
 * different value. */
export const DEFAULT_STALE_THRESHOLD_MS = 15 * 60_000;

/** Milliseconds elapsed between `createdAt` (ISO-8601) and `now` (defaults to the real
 * current time — overridable for deterministic tests). */
export function ageMs(createdAt: string, now: Date = new Date()): number {
  return now.getTime() - new Date(createdAt).getTime();
}

export function isAgedBeyondThreshold(
  createdAt: string,
  thresholdMs: number = DEFAULT_STALE_THRESHOLD_MS,
  now: Date = new Date(),
): boolean {
  return ageMs(createdAt, now) > thresholdMs;
}

/** Compact human-readable age, e.g. "42m", "3h 12m", "2d 4h" — used by every queue table's
 * "Age" column so an operator can scan for staleness without doing timestamp math in
 * their head. */
export function formatAge(ms: number): string {
  if (ms < 0) return "0m";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

export interface AgedItem<T> {
  item: T;
  ageMs: number;
  agedBeyondThreshold: boolean;
}

/**
 * Annotates a list of queue items (anything with a `created_at` ISO timestamp) with real
 * age + threshold-breach data, and sorts oldest-first — the operator's actual triage
 * order ("what's been sitting longest"), not fixture insertion order.
 */
export function annotateQueueAge<T extends { created_at: string }>(
  items: readonly T[],
  thresholdMs: number = DEFAULT_STALE_THRESHOLD_MS,
  now: Date = new Date(),
): AgedItem<T>[] {
  return items
    .map((item) => {
      const ms = ageMs(item.created_at, now);
      return { item, ageMs: ms, agedBeyondThreshold: ms > thresholdMs };
    })
    .sort((a, b) => b.ageMs - a.ageMs);
}

/** Count of items past the threshold — the number a queue summary tile/alert badge shows. */
export function countAgedBeyondThreshold<T extends { created_at: string }>(
  items: readonly T[],
  thresholdMs: number = DEFAULT_STALE_THRESHOLD_MS,
  now: Date = new Date(),
): number {
  return items.filter((item) => isAgedBeyondThreshold(item.created_at, thresholdMs, now)).length;
}
